import React, { useEffect } from "react";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Modal from "@mui/material/Modal";
import { trackActivity } from "../utils/mixpanel";
import {
  AI_CONSENT_POLICY_LINKS,
  AI_CONSENT_PROVIDER,
  formatProviderDisplayName
} from "./consentConstants";

const consentBullets = [
  {
    title: "Verify important information",
    body:
      "AI-generated responses may be inaccurate, incomplete or outdated. Check important information against SPARTA records, source documents or appropriate subject-matter experts."
  },
  {
    title: "Use authorised information only",
    body:
      "Do not enter passwords, authentication credentials, payment-card information, sensitive personal information unrelated to the business purpose, or information you are not authorised to disclose."
  },
  {
    title: "Use AI responsibly",
    body:
      "AI assistance does not replace professional judgement, approved procedures or required internal approvals."
  },
  {
    title: "AI data processing",
    body:
      "Information submitted to the AI Chat Assistant may be processed by Acumen/SPARTA systems and approved third-party AI service providers for the purpose of generating responses and operating the service."
  }
];

export default function AiConsentModal({
  open,
  submitting = false,
  message = "",
  reacceptance = false,
  policy = null,
  onAccept,
  onCancel
}) {
  const providerLabel = formatProviderDisplayName(
    policy?.provider_display_name || AI_CONSENT_PROVIDER.provider_display_name
  );

  const policyLinks = [
    {
      label: "Terms of Use",
      href: AI_CONSENT_POLICY_LINKS.termsOfUse,
      event: "ai_terms_link_clicked"
    },
    {
      label: "AI & Data Use",
      href: AI_CONSENT_POLICY_LINKS.aiDataUse,
      event: "ai_data_policy_link_clicked"
    },
    {
      label: "Privacy Policy",
      href: AI_CONSENT_POLICY_LINKS.privacyPolicy,
      event: "ai_privacy_policy_link_clicked"
    }
  ];

  useEffect(() => {
    if (!open) return;
    trackActivity(reacceptance ? "ai_reconsent_requested" : "ai_consent_modal_viewed", {
      page_title: "AI Chat Assistant"
    });
  }, [open, reacceptance]);

  return (
    <Modal
      open={open}
      disableEscapeKeyDown
      aria-labelledby="ai-consent-title"
      aria-describedby="ai-consent-description"
      slotProps={{ backdrop: { className: "consent-modal-backdrop" } }}
      sx={{
        alignItems: "center",
        display: "flex",
        justifyContent: "center",
        p: { xs: 1.5, sm: 3 },
        zIndex: 1500
      }}
    >
      <Box className="consent-modal" component="section" tabIndex={-1} sx={{ outline: "none" }}>
        <p className="consent-provider-label">Powered by {providerLabel}</p>
        <h2 id="ai-consent-title">Before you use AI Chat Assistant</h2>
        <p id="ai-consent-description" className="consent-intro">
          SPARTA&apos;s AI Chat Assistant uses artificial intelligence, including approved third-party
          AI services, to help you find and analyse information.
        </p>

        <ul className="consent-bullet-list">
          {consentBullets.map((item) => (
            <li key={item.title}>
              <strong>{item.title}:</strong> {item.body}
            </li>
          ))}
        </ul>

        <p className="consent-confirmation-copy">
          By selecting <strong>I Agree &amp; Continue</strong>, you confirm that you have read and
          understood these conditions and agree to use the AI Chat Assistant in accordance with the
          applicable policies.
        </p>

        <div className="consent-policy-links">
          {policyLinks.map((link, index) => (
            <React.Fragment key={link.label}>
              {index > 0 && <span aria-hidden="true">·</span>}
              <a
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackActivity(link.event, { page_title: "AI Chat Assistant" })}
              >
                {link.label}
              </a>
            </React.Fragment>
          ))}
        </div>

        {message ? (
          <p className="consent-notice" role="status">
            {message}
          </p>
        ) : null}

        <div className="consent-modal-actions">
          <button type="button" className="secondary-action" onClick={onCancel} disabled={submitting}>
            Cancel
          </button>
          <button type="button" className="primary-action" onClick={onAccept} disabled={submitting}>
            {submitting ? (
              <>
                <CircularProgress size={14} color="inherit" />
                Saving…
              </>
            ) : (
              "I Agree & Continue"
            )}
          </button>
        </div>
      </Box>
    </Modal>
  );
}
